// ManageFormations.jsx – version design amélioré + anti-cache
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle, faCalendarPlus,
  faFilter, faSync, faImage, faTimes, faSpinner, faMoneyBills,
  faDollarSign, faTag, faClock, faUsers, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { FormationsContext } from '../layout/CunstomFormations';

const API_URL_FORMATION = `${API_CONFIG2.apiUrl}formation/api_formation.php`;
const API_URL_SESSION = `${API_CONFIG2.apiUrl}session/api_session.php`;
const API_IMAGE = `${API_CONFIG2.apiUrl}`;

// ---- Dexie ----
const db = new Dexie('FormationsCache');
db.version(1).stores({ cache: 'key, timestamp' });
const CACHE_TTL = 24 * 60 * 60 * 1000;
const CACHE_KEY = 'formations_list';

// ---- Dédoublonnage ----
const pendingFetches = new Map();

// ============================================================
// UTILITAIRE : fetch avec parsing JSON sécurisé
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 100)}`);
  }
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageFormations = () => {
  const { refreshKey, setSource } = useContext(FormationsContext) || {};

  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });

  const [filters, setFilters] = useState({
    categorie: '',
    dureeMin: '',
    dureeMax: '',
    participantsMin: '',
    participantsMax: '',
    prix: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null);

  const [formData, setFormData] = useState({
    id: '',
    titre: '',
    description: '',
    categorie: '',
    duree: '',
    participantsLimit: 0,
    prix: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [sessionData, setSessionData] = useState({
    id_formation: '',
    mois: '',
    debut: '',
    fin: ''
  });

  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success'
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const searchTimeoutRef = useRef(null);
  const filterTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // ============================================================
  // CACHE HELPERS
  // ============================================================
  const getCache = async (key) => {
    try {
      const cached = await db.cache.get(key);
      if (cached && cached.timestamp && (Date.now() - cached.timestamp) < CACHE_TTL) {
        return cached.data;
      }
      return null;
    } catch { return null; }
  };

  const setCache = async (key, data) => {
    try {
      await db.cache.put({ key, data, timestamp: Date.now() });
    } catch { /* ignore */ }
  };

  const invalidateCache = async (key) => {
    try {
      await db.cache.delete(key);
    } catch { /* ignore */ }
  };

  // ============================================================
  // RÉCUPÉRATION DES FORMATIONS (avec anti-cache + dédoublonnage)
  // ============================================================
  const fetchFormations = useCallback(
    async (page = 1, search = searchTerm, force = false, retryCount = 0, limit = pagination.limit) => {
      // Annuler la requête précédente
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Paramètre anti-cache : timestamp
      const cacheBuster = new Date().getTime();

      // Clé de cache incluant le timestamp pour forcer le rafraîchissement si besoin
      const cacheKey = `${CACHE_KEY}_${page}_${limit}_${search}_${JSON.stringify(filters)}_${force ? cacheBuster : ''}`;
      const requestKey = force ? `${cacheKey}_force` : cacheKey;

      // Dédoublonnage
      if (pendingFetches.has(requestKey) && !force) {
        try {
          const result = await pendingFetches.get(requestKey);
          if (result) {
            setFormations(result.data);
            setPagination(result.pagination);
            if (setSource) setSource('cache (partagé)');
          }
          return;
        } catch {
          pendingFetches.delete(requestKey);
        }
      }

      if (retryCount > 5) {
        setLoading(false);
        setToast({
          show: true,
          message: 'Erreur : trop de tentatives. Veuillez réessayer plus tard.',
          variant: 'danger'
        });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
        return;
      }

      // 1. Cache (sauf si force)
      if (!force) {
        const cached = await getCache(cacheKey);
        if (cached) {
          setFormations(cached.data);
          setPagination(cached.pagination);
          if (setSource) setSource('cache');
          setLoading(false);
          return;
        }
      }

      // 2. Requête réseau
      setLoading(true);
      const fetchPromise = (async () => {
        try {
          const params = new URLSearchParams({
            action: 'fetch',
            page,
            limit,
            search,
            _t: cacheBuster // Anti-cache
          });
          if (filters.categorie) params.append('categorie', filters.categorie);
          if (filters.dureeMin) params.append('dureeMin', filters.dureeMin);
          if (filters.dureeMax) params.append('dureeMax', filters.dureeMax);
          if (filters.participantsMin) params.append('participantsMin', filters.participantsMin);
          if (filters.participantsMax) params.append('participantsMax', filters.participantsMax);
          if (filters.prix) params.append('prix', filters.prix);

          const url = `${API_URL_FORMATION}?${params.toString()}`;
          const response = await fetch(url, { signal: controller.signal });

          if (response.status === 429) {
            const stale = await getCache(cacheKey);
            if (stale) {
              setFormations(stale.data);
              setPagination(stale.pagination);
              if (setSource) setSource('cache (périmé)');
              setToast({
                show: true,
                message: '429 – Utilisation du cache (peut être obsolète).',
                variant: 'warning'
              });
              setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
              return stale;
            }
            const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            return fetchFormations(page, search, force, retryCount + 1, limit);
          }

          if (!response.ok) {
            const stale = await getCache(cacheKey);
            if (stale) {
              setFormations(stale.data);
              setPagination(stale.pagination);
              if (setSource) setSource('cache (périmé)');
              setToast({
                show: true,
                message: `Erreur réseau – Utilisation du cache`,
                variant: 'warning'
              });
              setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
              return stale;
            }
            throw new Error(`HTTP ${response.status}`);
          }

          const text = await response.text();
          let data;
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            throw new Error(`Réponse JSON invalide : ${text.substring(0, 100)}`);
          }

          if (data.status === 'success') {
            const result = { data: data.data, pagination: data.pagination };
            setFormations(data.data);
            setPagination(data.pagination);
            if (setSource) setSource(force ? 'live (forcé)' : 'live');
            await setCache(cacheKey, result);
            if (force) {
              setToast({ show: true, message: 'Données actualisées', variant: 'success' });
              setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
            }
            setLoading(false);
            return result;
          } else {
            throw new Error(data.message || 'Erreur inconnue');
          }
        } catch (err) {
          if (err.name === 'AbortError') return null;
          const stale = await getCache(cacheKey);
          if (stale) {
            setFormations(stale.data);
            setPagination(stale.pagination);
            if (setSource) setSource('cache (périmé)');
            setToast({
              show: true,
              message: `Erreur – Utilisation du cache : ${err.message}`,
              variant: 'warning'
            });
            setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
            setLoading(false);
            return stale;
          }
          setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
          setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
          setLoading(false);
          throw err;
        }
      })();

      pendingFetches.set(requestKey, fetchPromise);
      try {
        const result = await fetchPromise;
        return result;
      } finally {
        pendingFetches.delete(requestKey);
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [pagination.limit, searchTerm, filters, setSource]
  );

  // ============================================================
  // GESTIONNAIRES RECHERCHE / FILTRES
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchFormations(1, val, false);
    }, 400);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
    filterTimeoutRef.current = setTimeout(() => {
      fetchFormations(1, searchTerm, false);
    }, 400);
  };

  const changePage = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchFormations(newPage, searchTerm, false);
  };

  const changeLimit = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchFormations(1, searchTerm, false, 0, newLimit);
  };

  // ============================================================
  // RÉACTION AU REFRESH GLOBAL
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchFormations(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, pagination.currentPage, searchTerm, fetchFormations]);

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = async () => {
    try {
      if (formations.length === 0) {
        setToast({ show: true, message: 'Aucune donnée à exporter', variant: 'warning' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        return;
      }
      const headers = ['ID', 'Titre', 'Description', 'Catégorie', 'Durée', 'Participants max', 'Prix'];
      const rows = formations.map(f => [
        f.id, f.titre, f.description, f.categorie, f.duree, f.participantsLimit, f.prix || 0
      ]);
      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `formations_page_${pagination.currentPage}.csv`;
      link.click();
      setToast({ show: true, message: 'Export CSV réussi', variant: 'success' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    } catch (err) {
      console.error(err);
      setToast({ show: true, message: 'Erreur lors de l\'export', variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    }
  };

  // ============================================================
  // CRUD FORMATION
  // ============================================================
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const action = editingFormation ? 'update' : 'create';
    const simulatedMethod = editingFormation ? 'PUT' : 'POST';

    const formDataToSend = new FormData();
    formDataToSend.append('_method', simulatedMethod);
    if (editingFormation && formData.id) {
      formDataToSend.append('id', formData.id);
    }
    formDataToSend.append('titre', formData.titre);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('categorie', formData.categorie);
    formDataToSend.append('duree', formData.duree);
    formDataToSend.append('participantsLimit', formData.participantsLimit);
    formDataToSend.append('prix', formData.prix);
    if (imageFile) {
      formDataToSend.append('image', imageFile);
    }

    try {
      const url = `${API_URL_FORMATION}?action=${action}&_t=${new Date().getTime()}`;
      const data = await fetchJSON(url, {
        method: 'POST',
        body: formDataToSend
      });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowModal(false);
        setImageFile(null);
        setImagePreview(null);
        await invalidateCache(CACHE_KEY);
        fetchFormations(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      console.error(err);
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const formData = new FormData();
      formData.append('_method', 'DELETE');
      formData.append('id', formData.id);

      const url = `${API_URL_FORMATION}?action=delete&_t=${new Date().getTime()}`;
      const data = await fetchJSON(url, {
        method: 'POST',
        body: formData
      });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowDeleteModal(false);
        await invalidateCache(CACHE_KEY);
        fetchFormations(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      console.error(err);
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setDeleting(false);
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setToast({ show: true, message: 'Veuillez sélectionner un fichier CSV', variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return;
    }
    setSubmitting(true);
    const formDataFile = new FormData();
    formDataFile.append('file', selectedFile);

    try {
      const url = `${API_URL_FORMATION}?action=import&_t=${new Date().getTime()}`;
      const data = await fetchJSON(url, {
        method: 'POST',
        body: formDataFile
      });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowImportModal(false);
        setSelectedFile(null);
        await invalidateCache(CACHE_KEY);
        fetchFormations(1, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      console.error(err);
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // GESTION SESSION
  // ============================================================
  const openSessionModal = (formation) => {
    setSessionData({
      id_formation: formation.id,
      mois: '',
      debut: '',
      fin: ''
    });
    setShowSessionModal(true);
  };

  const handleSessionSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = `${API_URL_SESSION}?action=create&_t=${new Date().getTime()}`;
      const data = await fetchJSON(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionData)
      });
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
        setShowSessionModal(false);
        await invalidateCache(CACHE_KEY);
        fetchFormations(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      console.error(err);
      setToast({ show: true, message: `Erreur : ${err.message}`, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // OUVERTURE / FERMETURE MODALES
  // ============================================================
  const openEditModal = (formation) => {
    setEditingFormation(formation);
    setFormData({ ...formation });
    if (formation.image) {
      setImagePreview(`${API_IMAGE}/${formation.image}`);
    } else {
      setImagePreview(null);
    }
    setImageFile(null);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingFormation(null);
    setFormData({
      id: '',
      titre: '',
      description: '',
      categorie: '',
      duree: '',
      participantsLimit: 0,
      prix: '',
    });
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImageFile(null);
      setImagePreview(null);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    const input = document.getElementById('imageInput');
    if (input) input.value = '';
  };

  // ============================================================
  // INITIALISATION ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    fetchFormations(1, '', false);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (filterTimeoutRef.current) clearTimeout(filterTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // RENDU AVEC DESIGN AMÉLIORÉ
  // ============================================================
  const formatPrice = (price) => {
    if (!price && price !== 0) return 'Gratuit';
    return `${Number(price).toFixed(2)} USD`;
  };

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
          --price-bg: rgba(59, 130, 246, 0.15);
          --price-color: #60a5fa;
        }
        .monetra-page { background: var(--bg-gradient); min-height: 100vh; padding: 30px; color: var(--text-primary); font-family: 'Inter', sans-serif; }
        .glass-card { background: var(--card-bg); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border: 1px solid var(--card-border); border-radius: 24px; padding: 1.8rem; box-shadow: var(--shadow); transition: all 0.2s ease; }
        .glass-card:hover { border-color: rgba(59, 130, 246, 0.25); box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05); }
        .monetra-btn { padding: 10px 24px; border-radius: 30px; border: 1px solid rgba(255,255,255,0.06); background: rgba(255,255,255,0.04); color: var(--text-primary); transition: all 0.25s ease; display: inline-flex; align-items: center; gap: 8px; font-weight: 500; cursor: pointer; font-size: 0.9rem; white-space: nowrap; }
        .monetra-btn:hover { background: rgba(255,255,255,0.08); transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.2); }
        .monetra-btn.primary { background: var(--accent-blue); color: #fff; border: none; box-shadow: 0 6px 20px var(--accent-blue-glow); }
        .monetra-btn.primary:hover { background: var(--accent-dark-blue); box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4); }
        .monetra-btn.danger { border-color: #ef4444; color: #f87171; }
        .monetra-btn.danger:hover { background: rgba(239, 68, 68, 0.12); }
        .monetra-btn.outline-secondary { border-color: rgba(255,255,255,0.08); color: var(--text-secondary); }
        .monetra-btn.outline-secondary:hover { background: rgba(255,255,255,0.05); }
        .monetra-btn.icon-only { padding: 8px 12px; font-size: 1rem; }
        .monetra-btn:disabled { opacity: 0.5; pointer-events: none; }
        .search-wrapper { position: relative; display: flex; align-items: center; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 30px; padding: 4px 4px 4px 20px; transition: all 0.3s ease; }
        .search-wrapper:focus-within { border-color: var(--accent-blue); box-shadow: 0 0 20px rgba(59, 130, 246, 0.08); }
        .search-wrapper input { background: transparent; border: none; color: var(--text-primary); outline: none; padding: 10px 0; flex: 1; }
        .search-wrapper input::placeholder { color: var(--text-muted); }
        .filter-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-top: 12px; }
        .filter-row .monetra-input-sm { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; color: var(--text-primary); padding: 6px 14px; font-size: 0.85rem; width: auto; min-width: 120px; transition: 0.2s; }
        .filter-row .monetra-input-sm:focus { border-color: var(--accent-blue); outline: none; background: rgba(255,255,255,0.06); }
        .formations-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1.5rem; }
        .formation-card { background: var(--card-bg); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border: 1px solid var(--card-border); border-radius: 24px; overflow: hidden; transition: all 0.3s ease; display: flex; flex-direction: column; box-shadow: var(--shadow); position: relative; }
        .formation-card:hover { transform: translateY(-6px); border-color: rgba(59, 130, 246, 0.3); box-shadow: 0 20px 60px rgba(59, 130, 246, 0.10); }
        .formation-card .card-image { width: 100%; height: 160px; background: rgba(255,255,255,0.02); display: flex; align-items: center; justify-content: center; overflow: hidden; border-bottom: 1px solid rgba(255,255,255,0.04); position: relative; }
        .formation-card .card-image img { width: 100%; height: 100%; object-fit: cover; }
        .formation-card .card-image .no-image { color: var(--text-muted); font-size: 2.5rem; opacity: 0.3; }
        .formation-card .price-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: var(--price-bg);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 20px;
          padding: 4px 14px;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--price-color);
          display: flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .formation-card .card-body { padding: 1.2rem 1.5rem; flex: 1; }
        .formation-card .card-body .card-title { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.5rem; }
        .formation-card .card-body .card-meta { display: flex; flex-wrap: wrap; gap: 0.5rem 1rem; font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 0.75rem; }
        .formation-card .card-body .card-meta span { display: flex; align-items: center; gap: 4px; }
        .formation-card .card-body .card-description { font-size: 0.9rem; color: var(--text-secondary); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; margin-bottom: 0.75rem; }
        .formation-card .card-actions { padding: 0.8rem 1.5rem 1.2rem; border-top: 1px solid rgba(255,255,255,0.04); display: flex; justify-content: flex-end; gap: 6px; flex-wrap: wrap; }
        .pagination-wrapper { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; padding: 1rem 0 0.5rem; gap: 12px; }
        .pagination-controls { display: flex; gap: 6px; align-items: center; }
        .pagination-controls .page-btn { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 30px; color: var(--text-primary); padding: 6px 14px; transition: 0.2s; cursor: pointer; }
        .pagination-controls .page-btn:hover { background: rgba(255,255,255,0.08); }
        .pagination-controls .page-btn.active { background: var(--accent-blue); color: #fff; border-color: var(--accent-blue); }
        .pagination-controls .page-btn:disabled { opacity: 0.3; pointer-events: none; }
        .monetra-modal .modal-content { background: rgba(10, 20, 45, 0.92); backdrop-filter: blur(30px); -webkit-backdrop-filter: blur(30px); border: 1px solid rgba(59, 130, 246, 0.12); border-radius: 28px; color: var(--text-primary); box-shadow: 0 30px 80px rgba(0,0,0,0.8); }
        .monetra-modal .modal-header { border-bottom: 1px solid rgba(59, 130, 246, 0.06); }
        .monetra-modal .btn-close { filter: invert(1) brightness(200%); opacity: 0.5; }
        .monetra-input { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; color: var(--text-primary); padding: 12px; width: 100%; transition: 0.3s; }
        .monetra-input:focus { border-color: var(--accent-blue); outline: none; background: rgba(255,255,255,0.06); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06); }
        .monetra-input::placeholder { color: var(--text-muted); }
        .monetra-input-sm { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; color: var(--text-primary); padding: 6px 14px; font-size: 0.85rem; width: auto; min-width: 120px; transition: 0.2s; }
        .monetra-input-sm:focus { border-color: var(--accent-blue); outline: none; }
        .image-upload-wrapper { display: flex; flex-direction: column; gap: 10px; }
        .image-upload-preview { position: relative; width: 100%; height: 180px; border-radius: 16px; overflow: hidden; background: rgba(255,255,255,0.02); border: 1px dashed rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; }
        .image-upload-preview img { width: 100%; height: 100%; object-fit: cover; }
        .image-upload-preview .placeholder-text { color: var(--text-muted); font-size: 0.9rem; opacity: 0.5; }
        .image-upload-preview .remove-image-btn { position: absolute; top: 8px; right: 8px; background: rgba(0,0,0,0.6); border: none; border-radius: 50%; color: #fff; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; }
        .image-upload-preview .remove-image-btn:hover { background: rgba(0,0,0,0.8); }
        .image-upload-input { padding: 8px; }
        .toast-monetra { background: rgba(10, 20, 45, 0.8) !important; backdrop-filter: blur(24px) !important; -webkit-backdrop-filter: blur(24px) !important; border: 1px solid rgba(59, 130, 246, 0.2) !important; border-radius: 16px !important; color: var(--text-primary) !important; box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important; }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .text-primary { color: var(--accent-blue); }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .px-3 { padding-left: 1rem; padding-right: 1rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .flex-grow-1 { flex-grow: 1; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .w-100 { width: 100%; }
        @media (max-width: 768px) {
          .monetra-page { padding: 16px; }
          .glass-card { padding: 1.2rem; }
          .filter-row .monetra-input-sm { min-width: 90px; }
          .pagination-wrapper { flex-direction: column; align-items: stretch; }
          .formations-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="monetra-page">
        {/* En-tête */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <h2 className="m-0" style={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
            <span style={{ color: 'var(--accent-blue)' }}>Gestion</span> des formations
          </h2>
          <div className="d-flex flex-wrap gap-2">
            <button className="monetra-btn primary" onClick={openCreateModal} disabled={loading}>
              <FontAwesomeIcon icon={faPlus} /> Nouvelle formation
            </button>
            <button className="monetra-btn" onClick={() => setShowImportModal(true)} disabled={loading}>
              <FontAwesomeIcon icon={faFileCsv} /> Importer CSV
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={loading || formations.length === 0}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
          </div>
        </div>

        {/* Recherche et filtres */}
        <div className="glass-card mb-4">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="search-wrapper flex-grow-1" style={{ maxWidth: '400px' }}>
              <FontAwesomeIcon icon={faSearch} className="text-secondary me-2" />
              <input
                type="text"
                placeholder="Rechercher une formation..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <button className="monetra-btn" onClick={() => setShowFilters(!showFilters)} disabled={loading}>
              <FontAwesomeIcon icon={faFilter} /> Filtres
            </button>
            <button
              className="monetra-btn"
              onClick={() => {
                setFilters({ categorie: '', dureeMin: '', dureeMax: '', participantsMin: '', participantsMax: '', prix: '' });
                setSearchTerm('');
                fetchFormations(1, '', true);
              }}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSync} /> Réinitialiser
            </button>
          </div>

          {showFilters && (
            <div className="filter-row">
              <input
                type="text"
                className="monetra-input-sm"
                placeholder="Catégorie"
                name="categorie"
                value={filters.categorie}
                onChange={handleFilterChange}
              />
              <input
                type="number"
                className="monetra-input-sm"
                placeholder="Durée min"
                name="dureeMin"
                value={filters.dureeMin}
                onChange={handleFilterChange}
              />
              <input
                type="number"
                className="monetra-input-sm"
                placeholder="Durée max"
                name="dureeMax"
                value={filters.dureeMax}
                onChange={handleFilterChange}
              />
              <input
                type="number"
                className="monetra-input-sm"
                placeholder="Participants min"
                name="participantsMin"
                value={filters.participantsMin}
                onChange={handleFilterChange}
              />
              <input
                type="number"
                className="monetra-input-sm"
                placeholder="Participants max"
                name="participantsMax"
                value={filters.participantsMax}
                onChange={handleFilterChange}
              />
              <input
                type="number"
                className="monetra-input-sm"
                placeholder="Prix max (USD)"
                name="prix"
                value={filters.prix}
                onChange={handleFilterChange}
              />
            </div>
          )}
        </div>

        {/* Grille des formations */}
        <div className="glass-card p-3">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-secondary mt-2">Chargement des formations...</p>
            </div>
          ) : formations.length === 0 ? (
            <div className="text-center py-5 text-secondary">
              <FontAwesomeIcon icon={faInfoCircle} size="2x" className="mb-2 opacity-50" />
              <p>Aucune formation trouvée.</p>
            </div>
          ) : (
            <div className="formations-grid">
              {formations.map(form => (
                <div className="formation-card" key={form.id}>
                  <div className="card-image">
                    {form.image ? (
                      <img
                        src={`${API_IMAGE}/${form.image}`}
                        alt={form.titre}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML =
                            '<div class="no-image"><FontAwesomeIcon icon={faImage} /></div>';
                        }}
                      />
                    ) : (
                      <div className="no-image">
                        <FontAwesomeIcon icon={faImage} />
                      </div>
                    )}
                    <div className="price-badge">
                      <FontAwesomeIcon icon={faMoneyBills} />
                      {formatPrice(form.prix)}
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="card-title">{form.titre}</div>
                    <div className="card-meta">
                      <span>
                        <FontAwesomeIcon icon={faTag} />{' '}
                        {form.categorie || 'Non catégorisé'}
                      </span>
                      <span>
                        <FontAwesomeIcon icon={faClock} /> {form.duree}
                      </span>
                      <span>
                        <FontAwesomeIcon icon={faUsers} /> {form.participantsLimit}
                      </span>
                    </div>
                    <div className="card-description">{form.description}</div>
                  </div>
                  <div className="card-actions">
                    <button
                      className="monetra-btn icon-only"
                      onClick={() => openSessionModal(form)}
                      title="Ajouter une session"
                    >
                      <FontAwesomeIcon icon={faCalendarPlus} />
                    </button>
                    <button
                      className="monetra-btn icon-only"
                      onClick={() => openEditModal(form)}
                      title="Modifier"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="monetra-btn icon-only danger"
                      onClick={() => {
                        setFormData(form);
                        setShowDeleteModal(true);
                      }}
                      title="Supprimer"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          <div className="pagination-wrapper">
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary small">
                {pagination.totalRecords} enregistrements
              </span>
              <select
                className="monetra-input-sm"
                value={pagination.limit}
                onChange={changeLimit}
                style={{ width: '80px' }}
                disabled={loading}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="pagination-controls">
              <button
                className="page-btn"
                onClick={() => changePage(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1 || loading}
              >
                Précédent
              </button>
              {[...Array(Math.min(pagination.totalPages, 10)).keys()].map(num => {
                const pageNum = num + 1;
                return (
                  <button
                    key={pageNum}
                    className={`page-btn ${pagination.currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => changePage(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {pagination.totalPages > 10 && <span className="text-muted">…</span>}
              <button
                className="page-btn"
                onClick={() => changePage(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages || loading}
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modales */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal" size="lg">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faEdit} className="text-primary me-2" />
              {editingFormation ? 'Modifier' : 'Ajouter'} une formation
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <label className="text-secondary small">Titre *</label>
                <input
                  className="monetra-input"
                  name="titre"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="col-12">
                <label className="text-secondary small">Description *</label>
                <textarea
                  className="monetra-input"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Catégorie *</label>
                <select
                  className="monetra-input"
                  name="categorie"
                  value={formData.categorie}
                  onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                  required
                  disabled={submitting}
                >
                  <option className="bg-dark" value="">-- Sélectionnez --</option>
                  <option className="bg-dark" value="Informatique">Informatique</option>
                  <option className="bg-dark" value="Bureautique">Bureautique</option>
                  <option className="bg-dark" value="Développement Web">Développement Web</option>
                  <option className="bg-dark" value="Développement Mobile">Développement Mobile</option>
                  <option className="bg-dark" value="Intelligence Artificielle">Intelligence Artificielle</option>
                  <option className="bg-dark" value="Cybersécurité">Cybersécurité</option>
                  <option className="bg-dark" value="Réseaux Informatiques">Réseaux Informatiques</option>
                  <option className="bg-dark" value="Base de Données">Base de Données</option>
                  <option className="bg-dark" value="Data Science">Data Science</option>
                  <option className="bg-dark" value="Cloud Computing">Cloud Computing</option>
                  <option className="bg-dark" value="Bureautique Microsoft Office">Bureautique Microsoft Office</option>
                  <option className="bg-dark" value="Excel">Excel</option>
                  <option className="bg-dark" value="Word">Word</option>
                  <option className="bg-dark" value="PowerPoint">PowerPoint</option>
                  <option className="bg-dark" value="Marketing Digital">Marketing Digital</option>
                  <option className="bg-dark" value="Graphisme">Graphisme</option>
                  <option className="bg-dark" value="Design UI/UX">Design UI/UX</option>
                  <option className="bg-dark" value="Montage Vidéo">Montage Vidéo</option>
                  <option className="bg-dark" value="Gestion de Projet">Gestion de Projet</option>
                  <option className="bg-dark" value="Comptabilité">Comptabilité</option>
                  <option className="bg-dark" value="Finance">Finance</option>
                  <option className="bg-dark" value="Entrepreneuriat">Entrepreneuriat</option>
                  <option className="bg-dark" value="Ressources Humaines">Ressources Humaines</option>
                  <option className="bg-dark" value="Langues">Langues</option>
                  <option className="bg-dark" value="Développement Personnel">Développement Personnel</option>
                  <option className="bg-dark" value="Auto Ecole">Auto Ecole</option>
                  <option className="bg-dark" value="Autre">Autre</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Durée *</label>
                <input
                  className="monetra-input"
                  name="duree"
                  value={formData.duree}
                  onChange={(e) => setFormData({ ...formData, duree: e.target.value })}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Nombre max de participants *</label>
                <input
                  type="number"
                  className="monetra-input"
                  name="participantsLimit"
                  value={formData.participantsLimit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      participantsLimit: parseInt(e.target.value) || 0
                    })
                  }
                  required
                  min="0"
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Prix (USD) *</label>
                <input
                  type="number"
                  step="0.01"
                  className="monetra-input"
                  name="prix"
                  value={formData.prix}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      prix: e.target.value
                    })
                  }
                  required
                  min="0"
                  disabled={submitting}
                />
              </div>
              <div className="col-md-6">
                <label className="text-secondary small">Image</label>
                <div className="image-upload-wrapper">
                  <div className="image-upload-preview">
                    {imagePreview ? (
                      <>
                        <img src={imagePreview} alt="Aperçu" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={removeImage}
                          disabled={submitting}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </>
                    ) : (
                      <span className="placeholder-text">
                        <FontAwesomeIcon icon={faImage} className="me-2" />
                        Aucune image
                      </span>
                    )}
                  </div>
                  <input
                    type="file"
                    id="imageInput"
                    accept="image/*"
                    className="monetra-input image-upload-input"
                    onChange={handleImageChange}
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowModal(false)}
              className="monetra-btn outline-secondary"
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              variant="success"
              type="submit"
              className="monetra-btn primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer la formation <strong>{formData.titre}</strong> ?
          <br />
          <span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
            className="monetra-btn outline-secondary"
            disabled={deleting}
          >
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            className="monetra-btn danger"
            disabled={deleting}
          >
            {deleting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Suppression...
              </>
            ) : (
              'Confirmer la suppression'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleImportCSV}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faFileCsv} className="text-primary me-2" />
              Importer des données CSV
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-secondary small">
              Le fichier doit avoir les colonnes dans cet ordre :<br />
              <code>titre, description, categorie, duree, participantsLimit, prix</code>
            </p>
            <input
              type="file"
              accept=".csv"
              className="monetra-input"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              required
              disabled={submitting}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowImportModal(false)}
              className="monetra-btn outline-secondary"
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              variant="success"
              type="submit"
              className="monetra-btn primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Importation...
                </>
              ) : (
                'Importer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showSessionModal} onHide={() => setShowSessionModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleSessionSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faCalendarPlus} className="text-primary me-2" />
              Ajouter une session
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <label className="text-secondary small">Formation</label>
                <input className="monetra-input" value={`ID ${sessionData.id_formation}`} disabled />
                <input type="hidden" name="id_formation" value={sessionData.id_formation} />
              </div>
              <div className="col-md-4">
                <label className="text-secondary small">Mois *</label>
                <select
                  className="monetra-input"
                  value={sessionData.mois}
                  onChange={(e) => setSessionData({ ...sessionData, mois: e.target.value })}
                  required
                  disabled={submitting}
                >
                  <option className="bg-dark" value="">Choisir...</option>
                  <option className="bg-dark" value="Janvier">Janvier</option>
                  <option className="bg-dark" value="Février">Février</option>
                  <option className="bg-dark" value="Mars">Mars</option>
                  <option className="bg-dark" value="Avril">Avril</option>
                  <option className="bg-dark" value="Mai">Mai</option>
                  <option className="bg-dark" value="Juin">Juin</option>
                  <option className="bg-dark" value="Juillet">Juillet</option>
                  <option className="bg-dark" value="Août">Août</option>
                  <option className="bg-dark" value="Septembre">Septembre</option>
                  <option className="bg-dark" value="Octobre">Octobre</option>
                  <option className="bg-dark" value="Novembre">Novembre</option>
                  <option className="bg-dark" value="Décembre">Décembre</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="text-secondary small">Date de début *</label>
                <input
                  type="date"
                  className="monetra-input"
                  value={sessionData.debut}
                  onChange={(e) => setSessionData({ ...sessionData, debut: e.target.value })}
                  required
                  disabled={submitting}
                />
              </div>
              <div className="col-md-4">
                <label className="text-secondary small">Date de fin *</label>
                <input
                  type="date"
                  className="monetra-input"
                  value={sessionData.fin}
                  onChange={(e) => setSessionData({ ...sessionData, fin: e.target.value })}
                  required
                  disabled={submitting}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowSessionModal(false)}
              className="monetra-btn outline-secondary"
              disabled={submitting}
            >
              Annuler
            </Button>
            <Button
              variant="success"
              type="submit"
              className="monetra-btn primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Création...
                </>
              ) : (
                'Créer la session'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: 9999,
            maxWidth: '440px',
            width: '100%'
          }}
        >
          <div className={`toast-monetra p-3 ${toast.variant}`}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
              />
              <span className="flex-grow-1">{toast.message}</span>
              <button
                className="btn-close-custom"
                onClick={() => setToast({ ...toast, show: false })}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  opacity: 0.6,
                  fontSize: '1.2rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageFormations;